import { useControlledState, useEventCallback } from '@ixigo/iui/utils';
import cn from '@repo/shared/utils/cn';
import { useEffect, useMemo, useRef } from 'react';
import {
	WheelPicker,
	type WheelPickerOption,
	WheelPickerWrapper,
} from '../wheel-picker';
import { ActiveBorder } from './ActiveBorder';
import { useWheelTempSelection } from './hooks/use-wheel-temp-selection';
import { getClasses } from './styles';
import type { DateTimePickerClassNames } from './types';
import {
	buildMeridiemOptions,
	buildMinuteOptions,
	checkTimeInRange,
	getDefaultTime,
} from './utils';

export interface TimePickerProps {
	/** Uncontrolled initial value as HH:mm (24h). */
	defaultValue?: string;
	/** Controlled HH:mm (24h) value. */
	value?: string;
	/** Emits HH:mm (24h) string after clamping/rounding to step. */
	onChange?: (time: string) => void;
	/** Minute granularity for time wheel; mirrors HTML step semantics (minutes). */
	step?: number;
	/** Visible rows for the wheels. */
	visibleRows?: number;
	/** Inclusive lower bound time as HH:mm (24h) for the selected day. */
	min?: string;
	/** Inclusive upper bound time as HH:mm (24h) for the selected day. */
	max?: string;
	/** Optional class name for wrapper. */
	className?: string;

	/** Optional class name for wrapper. */
	classes?: DateTimePickerClassNames;
}

export function TimePicker({
	defaultValue,
	value,
	onChange,
	step = 1,
	visibleRows = 9,
	min,
	max,
	className,
	classes: classNames,
}: TimePickerProps) {
	const [finalValue, setTime] = useControlledState<string>({
		controlled: value,
		default: defaultValue ?? getDefaultTime(step, min, max),
	});

	const { tempValueByField, tryCommit, attachWheelStopListener } =
		useWheelTempSelection<'hour' | 'minute' | 'meridiem'>({
			fieldsWithDeferredRevert: ['meridiem'],
		});
	const currentSelectedValue = finalValue;

	// Ref to the top border element rendered by ActiveBorder (we'll use it to find
	// the meridiem wheel container since the wheel-picker component is internal).
	const separatorRef = useRef<HTMLDivElement>(null);

	const classes = getClasses(classNames);

	// derive display directly from current time string
	const [vhRaw, vmRaw] = (currentSelectedValue ?? '00:00')
		.split(':')
		.map((n) => Number(n));
	const baseHour: number = Number.isFinite(vhRaw) ? (vhRaw as number) : 0;
	const baseMinute: number = Number.isFinite(vmRaw) ? (vmRaw as number) : 0;

	const displayMeridiem: 'AM' | 'PM' = baseHour >= 12 ? 'PM' : 'AM';
	// Note: if step >= 60, behavior is undefined (assume 1-59). Minutes rounded to nearest step.
	const displayMinute = Math.round(baseMinute / step) * step;

	const displayHour = String(baseHour).padStart(2, '0');

	// 24-hour values for hour wheel; labels stay 12-hour for display semantics
	const hourOptions = useMemo<WheelPickerOption[]>(
		() =>
			Array.from({ length: 24 }, (_, h) => ({
				value: String(h).padStart(2, '0'),
				label: String(h % 12 || 12).padStart(2, '0'),
			})),
		[],
	);

	const minuteOptions = useMemo(() => buildMinuteOptions(step), [step]);
	const meridiemOptions: WheelPickerOption[] = useMemo(
		() => buildMeridiemOptions(),
		[],
	);

	const wheelVisibleCount = Math.max(8, Math.floor((visibleRows - 1) / 2) * 4);

	const violatesBounds = (hhmm: string): boolean => {
		return !checkTimeInRange(hhmm, min, max);
	};

	const pushChange = (
		h24: number,
		m: number,
		field: 'hour' | 'minute' | 'meridiem',
	) => {
		const rawHH = String(h24).padStart(2, '0');
		const rawMM = String(m).padStart(2, '0');
		const newValue = `${rawHH}:${rawMM}`;

		tryCommit({
			field,
			isValid: () => !violatesBounds(newValue),
			commit: () => {
				setTime(newValue);
				onChange?.(newValue);
			},
			displayValue:
				field === 'hour'
					? rawHH
					: field === 'minute'
						? rawMM
						: h24 >= 12
							? 'PM'
							: 'AM',
		});
	};

	// With 24-hour hour wheel, flipping AM/PM is just +/- 12 hours
	const toggleMeridiem = (h24: number): number => (h24 + 12) % 24;

	const commonWheelProps = {
		optionItemHeight: 50,
		visibleCount: wheelVisibleCount,
		classNames: classes,
	} as const;

	// attach wheel listener to meridiem wheel once after mount
	useEffect(() => {
		return attachWheelStopListener(separatorRef.current, 3, 'meridiem');
	}, [attachWheelStopListener]);

	const onMeridiemChange = useEventCallback(() => {
		const nextHour = toggleMeridiem(baseHour);
		pushChange(nextHour, displayMinute, 'meridiem');
	});

	return (
		<WheelPickerWrapper className={cn(classes.root, className)}>
			<WheelPicker
				options={hourOptions}
				value={tempValueByField.hour ?? displayHour}
				{...commonWheelProps}
				infinite
				onValueChange={(val: string) => {
					const newHour24 = Number(val);
					pushChange(newHour24, displayMinute, 'hour');
				}}
			/>
			<WheelPicker
				options={minuteOptions}
				value={tempValueByField.minute ?? displayMinute.toString()}
				{...commonWheelProps}
				infinite
				onValueChange={(val: string) => {
					const newMinute = Number(val);
					pushChange(baseHour, newMinute, 'minute');
				}}
			/>
			<WheelPicker
				options={meridiemOptions}
				value={tempValueByField.meridiem ?? displayMeridiem}
				{...commonWheelProps}
				onValueChange={onMeridiemChange}
			/>
			<ActiveBorder optionHeight={50} itemRef={separatorRef} />
		</WheelPickerWrapper>
	);
}
